/**
 * What `/admin/rooms` shows while its three loaders are in flight (UI1).
 *
 * WHAT THIS REPLACES. A single bordered box reading "Daten werden geladen …".
 * It was honest but it was also the whole page: the moment the data landed,
 * the toolbar, five cards, the unit rows and a nine-month matrix all appeared
 * at once and everything below the fold jumped. This holds roughly the shape
 * that is coming, so the arrival is a fill rather than a reflow.
 *
 * A STABILISER, NOT A PIXEL COPY. It mirrors the page's BLOCKS and its
 * breakpoints — five cards at the same `sm`/`xl`/`2xl` steps, three unit rows,
 * one calendar band — and nothing finer. Chasing an exact likeness would mean
 * a second layout to keep in sync with the first, and the first would win.
 *
 * ONE ANNOUNCEMENT, NOT THIRTY. The container is the `role="status"` the page
 * already had, and it carries the same sentence for a screen reader. Every
 * block below it is `aria-hidden`: a reader that met forty unlabelled boxes
 * would be worse off than one that met a single line of text, which is what
 * this is replacing.
 *
 * NO NEW COLOUR AND NO NEW ANIMATION. `ui/skeleton` already owns both — it is
 * `animate-pulse` over `bg-primary/10`, a token that follows the theme, so
 * dark mode needs nothing from this file.
 */
import React from "react";

import { Skeleton } from "../../../components/ui/skeleton";

/** The page's own loading sentence, unchanged from the text block it replaces. */
export const CO_LIVING_LOADING_TEXT = "Daten werden geladen …";

function CardSkeleton() {
  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3.5 shadow-sm">
      <Skeleton className="h-10 w-10 shrink-0 rounded-[10px]" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <Skeleton className="h-2.5 w-16" />
        <Skeleton className="h-5 w-10" />
        <Skeleton className="h-2 w-24" />
      </div>
    </div>
  );
}

function UnitRowSkeleton() {
  return (
    <div className="rounded-[12px] border border-border bg-card px-4 py-3.5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Skeleton className="h-11 w-11 shrink-0 rounded-[10px]" />
          <div className="flex min-w-0 flex-col gap-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-2.5 w-40" />
            <Skeleton className="h-2.5 w-24" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Skeleton className="h-5 w-16 rounded-md" />
          <Skeleton className="h-5 w-14 rounded-md" />
          <Skeleton className="h-5 w-20 rounded-md" />
          <Skeleton className="h-5 w-12 rounded-md" />
        </div>
        <div className="flex shrink-0 items-center gap-2 self-start sm:ml-auto sm:self-center">
          <Skeleton className="h-7 w-28 rounded-[8px]" />
          <Skeleton className="h-7 w-32 rounded-[8px]" />
        </div>
      </div>
    </div>
  );
}

export default function CoLivingPageSkeleton() {
  return (
    <div
      role="status"
      aria-live="polite"
      // A stable test seam, the same shape as `data-unit-badge` and
      // `data-room-card` elsewhere on this page: asserting "is the skeleton
      // up" must not depend on a class string that a restyle may move.
      data-page-skeleton
      className="flex flex-col gap-6"
    >
      <span className="sr-only">{CO_LIVING_LOADING_TEXT}</span>

      <div aria-hidden="true" className="flex flex-col gap-6">
        {/* Toolbar */}
        <div className="flex flex-col gap-[10px] rounded-[12px] border border-input bg-card px-4 py-3 shadow-sm md:flex-row md:items-center">
          <Skeleton className="h-4 min-w-0 flex-1" />
          <div className="hidden h-[22px] w-px shrink-0 bg-border md:block" />
          <div className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>

        {/* Kennzahlen — the same five slots at the same breakpoints. */}
        <div>
          <div className="mb-[10px] flex items-center gap-2">
            <Skeleton className="h-2.5 w-20" />
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        </div>

        {/* Einheiten & Zimmer */}
        <div>
          <div className="mb-[10px] flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3.5 w-56" />
              <Skeleton className="h-2.5 w-72" />
            </div>
            <Skeleton className="h-3 w-20" />
          </div>
          <div className="flex flex-col gap-2.5">
            {[0, 1, 2].map((i) => (
              <UnitRowSkeleton key={i} />
            ))}
          </div>
        </div>

        {/* Belegungskalender — one band, because the real matrix is tall and
            its arrival is the biggest shift on the page. */}
        <div>
          <div className="mb-[10px] flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-2.5 w-64" />
            </div>
            <Skeleton className="h-6 w-32 rounded-[8px]" />
          </div>
          <div className="overflow-hidden rounded-[12px] border border-border bg-card shadow-sm">
            <div className="border-b border-border px-4 py-3">
              <div className="flex flex-wrap gap-2">
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-3.5 w-24" />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 px-4 py-3">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[34px] w-full rounded-[6px]" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
